import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ElementRef, EventEmitter, Injector, Input, Output, TemplateRef, afterNextRender, computed, inject, input, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzDialogComponent } from "@template-ui/components/ez-dialog/ez-dialog.component";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@template-ui/types/ez-form.types";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { TerminologyFormIdentityDirective } from "@app/shared/terminology/terminology-form-identity.directive";
import { HEARING_DECISION_AMENDMENT_TERMINOLOGY } from "./sections/hearing-decision-entry.terminology";
import { catchError, of } from "rxjs";

import { BitsStateService } from "@app/core/services/bits-state.service";
import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";
import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PhmsDocument } from "../documents/phms-document.model";
import { PhmsDocumentService } from "../documents/phms-document.service";
import { PHMS_FACILITY_TERMINOLOGY } from "../phms-facility.terminology";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";
import { PHMS_DECISION_TERMINOLOGY } from "../phms-decision.terminology";
import { PHMS_DOCUMENT_TERMINOLOGY } from "../phms-document.terminology";
import { HearingRecordService } from "./hearing-record.service";
import { CaseContextRefreshService } from "@app/case-context/services/case-context-refresh.service";
import {
  HearingDecisionFactor,
  HearingDecisionFinalizeRequest,
  HearingDecisionOutcome,
  HearingRecordDto,
} from "./hearing-record.models";

/**
 * Decision permission for the post-decision amend (#PHMS-HREC S6, ADO #1508). The backend gates the
 * decision finalize/supersede on the dedicated hearing-decision permission (held by officials,
 * operations, and platform admins — never schedulers, who only author capture/documents); the surface
 * mirrors that so the Amend affordance is hidden/disabled exactly when the server would refuse it.
 */
const DECISION_RECORD_PERMISSION = "phms.hearing_decision.record";

/** Label/value rows the "Determining Factors Relied Upon" card renders, in mockup order. */
const FACTOR_ORDER: { code: string; label?: string }[] = [
  { code: "insight", label: "Insight" },
  { code: "cra_risk", label: "CRA risk rating" },
  { code: "institutional_conduct" },
  { code: "reentry_plans", label: "Reentry plans" },
  { code: "commitment_nexus", label: "Offense → dangerousness" },
];

/** A rendered determining-factor row for the card. */
export interface DeterminingFactorRow {
  code: string;
  label: string;
  value: string;
}

/**
 * Post-decision record + amend surface (#PHMS-HREC S6) — the "Decision" tab.
 *
 * A read-only, outcome-aware view over the cohesive S1 record's decision block
 * (`record.decision.outcome`, authored by the S5 finalize). The finding banner is colour-coded
 * (grant green / deny red), the rationale, determining factors, and special conditions render the
 * finalized wording, and the right rail is outcome-aware (release + conditions on a grant; denial
 * length + tentative next hearing on a denial; a Governor-review red flag on a PC 187 grant).
 *
 * Amend is permission-gated on {@link DECISION_RECORD_PERMISSION}: when the actor cannot record a
 * decision, the Amend button is hidden. When they can, clicking it opens the shared amendment modal that re-submits through
 * {@link HearingRecordService.finalizeDecision} — the backend supersedes the prior decision while the
 * already-finalized wording stays immutable. Renders a graceful empty state when no decision exists.
 */
@Component({
  selector: "phms-your-decision",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective, TerminologyFormIdentityDirective],
  templateUrl: "./your-decision.component.html",
  styleUrl: "./your-decision.component.scss",
})
export class YourDecisionComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);
  private readonly reloadDecisionTrigger = viewChild<unknown, ElementRef<HTMLElement>>("reloadDecisionTrigger", { read: ElementRef });
  private readonly amendTrigger = viewChild<unknown, ElementRef<HTMLElement>>("amendTrigger", { read: ElementRef });
  private amendmentDialog: MatDialogRef<EzDialogComponent> | null = null;
  private readonly amendmentTemplate = viewChild<TemplateRef<unknown>>("amendmentTemplate");
  private revision = 0;
  private documentRevision = 0;
  readonly externalBusy = input(false);
  @Output() readonly busyChanged = new EventEmitter<boolean>();
  @Output() readonly amended = new EventEmitter<void>();
  readonly loadError = signal<string | null>(null);
  readonly amendmentStale = signal(false);
  readonly entryText = createTerminologyUsageSignals(HEARING_DECISION_AMENDMENT_TERMINOLOGY);
  readonly formUsage = { rationale: HEARING_DECISION_AMENDMENT_TERMINOLOGY.rationale };
  readonly decisionUsage = PHMS_DECISION_TERMINOLOGY.yourDecision;
  constructor() { this.destroyRef.onDestroy(() => { this.amendmentDialog?.close(); this.busyChanged.emit(false); }); }

  private readonly roles = inject(RoleService);
  private readonly appState = inject(BitsStateService);
  private readonly appConfig = inject(ApplicationConfigurationService);
  private readonly recordService = inject(HearingRecordService);
  private readonly documentService = inject(PhmsDocumentService);
  private readonly caseContextRefresh = inject(CaseContextRefreshService);
  readonly facilityText = createTerminologyUsageSignals(PHMS_FACILITY_TERMINOLOGY.yourDecision);
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.yourDecision);
  readonly decisionText = createTerminologyUsageSignals(PHMS_DECISION_TERMINOLOGY.yourDecision);
  readonly documentText = createTerminologyUsageSignals(PHMS_DOCUMENT_TERMINOLOGY.yourDecision);

  /** The hosting proceeding. Setting it (re)loads the S1 record (and its decision block). */
  @Input({ required: true })
  set scheduledProceedingId(value: number | null) {
    this.revision++; this.documentRevision++;
    this.amendmentDialog?.close(); this.amendOpen.set(false); this.isAmending.set(false); this.busyChanged.emit(false);
    this._record.set(null); this.generatedDocuments.set([]); this.amendSubmitted.set(false); this.loadError.set(null);
    this._proceedingId.set(value);
    if (value !== null && value > 0) {
      this.load(value);
      this.loadDocuments(value);
    }
  }
  get scheduledProceedingId(): number | null {
    return this._proceedingId();
  }

  private readonly _proceedingId = signal<number | null>(null);
  private readonly _record = signal<HearingRecordDto | null>(null);

  readonly isLoading = signal(false);

  /** Decision permission gate (ADO #1508); Amend is hidden when false (mirrors the server's gate). */
  readonly canAmend = this.roles.hasAnyPermissionSignal(DECISION_RECORD_PERMISSION);

  /* --- Decision outcome (the S5-authored record the view binds). --- */
  readonly outcome = computed<HearingDecisionOutcome | null>(() => this._record()?.decision?.outcome ?? null);
  readonly hasDecision = computed(() => {
    const outcome = this.outcome();
    return outcome != null && (outcome.finding ?? "").trim().length > 0;
  });
  readonly finding = computed(() => (this.outcome()?.finding ?? "").trim().toLowerCase());
  readonly isGrant = computed(() => this.finding() === "suitable");
  readonly isDenial = computed(() => this.finding() === "unsuitable");
  readonly rationale = computed(() => (this.outcome()?.rationale ?? "").trim());
  readonly specialConditions = computed<string[]>(() =>
    (this.outcome()?.specialConditions ?? []).map((c) => (c ?? "").trim()).filter((c) => c.length > 0),
  );
  readonly denialLengthYears = computed(() => this.outcome()?.denialLengthYears ?? null);
  readonly proposedReleaseDate = computed(() => this.outcome()?.proposedReleaseDate ?? null);
  readonly governorReview = computed(() => (this.outcome()?.governorReview ?? false) || (this._record()?.derived?.governorReview ?? false));
  readonly adminReview = computed(() => this.outcome()?.adminReview ?? false);
  readonly recordedUtc = computed(() => this.outcome()?.recordedUtc ?? null);
  readonly recordedByUserId = computed(() => this.outcome()?.recordedByUserId ?? null);

  /** Recorder display label; falls back to the numeric id when no name is resolved on the record. */
  readonly recorderLabel = computed(() => {
    const id = this.recordedByUserId();
    return id != null && id > 0 ? `User #${id}` : "the panel";
  });

  /** Finding banner heading + theme (grant green / deny red). */
  readonly bannerHeading = computed(() => {
    if (this.isGrant()) {
      return "Granted — Found Suitable";
    }
    if (this.isDenial()) {
      return "Denied — Found Unsuitable";
    }
    return this.decisionText.decisionRecordedTitle();
  });
  readonly bannerTheme = computed<"grant" | "deny" | "neutral">(() => {
    if (this.isGrant()) {
      return "grant";
    }
    if (this.isDenial()) {
      return "deny";
    }
    return "neutral";
  });

  /** Tentative next hearing on a denial: recorded date + denial length. */
  readonly tentativeNextHearing = computed(() => {
    const years = this.denialLengthYears();
    const recorded = this.recordedUtc();
    if (years == null || !recorded) {
      return null;
    }
    const base = new Date(recorded);
    if (Number.isNaN(base.getTime())) {
      return null;
    }
    const next = new Date(base);
    next.setFullYear(next.getFullYear() + years);
    return next.toISOString();
  });

  /** Determining-factor rows in mockup order, joined onto the finalized factor findings. */
  readonly determiningFactorRows = computed<DeterminingFactorRow[]>(() => {
    const order = new Map(FACTOR_ORDER.map((factor, index) => [factor.code, index]));
    return [...(this.outcome()?.determiningFactors ?? [])]
      .filter((factor) => normalizeFactorCode(factor).length > 0)
      .sort((left, right) => (order.get(normalizeFactorCode(left)) ?? Number.MAX_SAFE_INTEGER)
        - (order.get(normalizeFactorCode(right)) ?? Number.MAX_SAFE_INTEGER))
      .map((factor) => {
      const code = normalizeFactorCode(factor);
      const definition = FACTOR_ORDER.find((candidate) => candidate.code === code);
      const value = (factor.findingText ?? factor.findingCode ?? "").trim() || "—";
      const resolvedLabel = code === "institutional_conduct"
        ? this.facilityText.conduct()
        : (definition?.label ?? titleCase(code));
      return {
        code,
        label: resolvedLabel,
        value,
      };
    });
  });

  /** Live generated outputs associated with this hearing; no placeholder rows are synthesized. */
  readonly generatedDocuments = signal<PhmsDocument[]>([]);
  readonly documentsLoading = signal(false);
  readonly decisionDocument = computed(
    () => this.generatedDocuments().find((document) => document.documentCategoryCode === "hearing-result") ?? null,
  );
  readonly decisionDocumentStatus = computed(() => {
    if (this.documentsLoading()) {
      return this.documentText.loadingGenerated();
    }
    if (this.decisionDocument()) {
      return this.decisionDocument()!.displayName;
    }
    return this.documentText.finalDecisionEmpty();
  });

  /* --- Amend (permission-gated supersede). --- */
  readonly amendOpen = signal(false);
  readonly isAmending = signal(false);
  readonly amendError = signal<string | null>(null);
  readonly amendSubmitted = signal(false);

  amendValues: AmendFormValues = createEmptyAmendValues();

  /**
   * Both amend gate drivers are signals — `amendValues` alone would not re-trigger the computed
   * when the rationale is typed after the finding is picked, leaving Submit stuck disabled.
   */
  readonly amendFinding = signal("");
  private readonly amendRationale = signal("");
  readonly amendIsUnsuitable = computed(() => this.amendFinding() === "unsuitable");
  readonly canSubmitAmend = computed(
    () => this.canAmend() && this.hasDecision() && !this.isLoading() && !this.isAmending() && !this.externalBusy() && !this.amendmentStale()
      && ["suitable", "unsuitable"].includes(this.amendFinding()) && this.amendRationale().trim().length > 0
      && (!this.amendIsUnsuitable() || toPositiveInt(this.amendDenialYears()) !== null),
  );

  readonly amendDenialYears = signal("");
  readonly findingConfig: InputConfig[] = [{ name: "finding", label: "Finding *", type: EzFormInputType.RADIO_GROUP_PLAIN, row: 1, width: "fill",
    options: [{ value: "suitable", viewValue: "Suitable" }, { value: "unsuitable", viewValue: "Unsuitable" }], validation: [Validators.required] }];
  readonly rationaleConfig = computed<InputConfig[]>(() => [{ name: "rationale", label: `${this.entryText.rationale()} *`, type: EzFormInputType.TEXTAREA, row: 1, width: "fill", textAreaRows: 4, floatLabel: "always",
    validation: [Validators.required, Validators.pattern(/\S/)], validationMessages: { required: "Enter the amended decision rationale.", pattern: "Enter the amended decision rationale." } }]);
  readonly conditionsConfig: InputConfig[] = [{ name: "specialConditions", label: "Special conditions (optional)", type: EzFormInputType.TEXTAREA, row: 1, width: "fill", textAreaRows: 3, floatLabel: "always" }];
  readonly denialConfig: InputConfig[] = [{ name: "denialLengthYears", label: "Denial length (years)", type: EzFormInputType.NUMBER, row: 1, width: "fill", floatLabel: "always",
    validation: [Validators.required, Validators.pattern(/^[1-9]\d*$/), Validators.max(2147483647)], validationMessages: { required: "Enter a denial length in whole years.", pattern: "Enter a positive whole number of years.", max: "Enter a valid number of years." } }];
  onAmendInput(event: InputChangeEvent<unknown>): void {
    if (!this.canAmend() || this.isAmending() || this.externalBusy() || this.amendmentStale()) return;
    const value = String(event.value ?? "");
    if (event.name === "finding") this.onAmendFindingChange(value);
    if (event.name === "rationale") { this.amendValues.rationale = value; this.onAmendRationaleChange(value); }
    if (event.name === "specialConditions") this.amendValues.specialConditions = value;
    if (event.name === "denialLengthYears") { this.amendValues.denialLengthYears = value; this.amendDenialYears.set(value); }
  }
  readonly pageTitle = this.decisionText.decision;

  /** Re-read generated outputs after Legal approval or another document-producing transition. */
  refreshDocuments(): void {
    const proceedingId = this._proceedingId();
    if (proceedingId !== null && proceedingId > 0) {
      this.loadDocuments(proceedingId);
    }
  }

  openDecisionPdf(): void {
    const document = this.decisionDocument();
    const path = document?.previewPath?.trim() || document?.accessPath?.trim();
    if (!path) {
      return;
    }
    window.open(this.appConfig.apiUrl(path), "_blank", "noopener,noreferrer");
  }

  /** Open the amendment modal, pre-filling it from the finalized outcome. */
  openAmend(): void {
    if (!this.canAmend() || !this.hasDecision() || this.isLoading() || this.isAmending() || this.externalBusy() || this.amendOpen() || this.amendmentStale()) {
      return;
    }
    const outcome = this.outcome();
    this.amendValues = {
      finding: this.finding(),
      rationale: this.rationale(),
      specialConditions: this.specialConditions().join("\n"),
      denialLengthYears: outcome?.denialLengthYears != null ? String(outcome.denialLengthYears) : "",
    };
    this.amendFinding.set(this.finding());
    this.amendRationale.set(this.rationale());
    this.amendDenialYears.set(this.amendValues.denialLengthYears);
    this.amendError.set(null);
    this.amendSubmitted.set(false);
    this.amendOpen.set(true); this.busyChanged.emit(true);
    const canSubmit = this.canSubmitAmend, busy = this.isAmending;
    const ref = this.dialog.open(EzDialogComponent, {
      width: "800px", maxWidth: "calc(100vw - 32px)", maxHeight: "90vh", autoFocus: "first-tabbable", restoreFocus: false,
      data: { heading: this.decisionText.amendDecision(), useForm: true, buttons: [], formActions: [
        { text: "Submit amendment", isSubmit: true, formId: "hearing-amendment-form", get disabled() { return !canSubmit(); }, action: () => {} },
        { text: "Cancel", get disabled() { return busy(); }, action: () => this.cancelAmend() },
      ] },
    });
    this.amendmentDialog = ref; ref.componentInstance.formValid = true;
    ref.componentRef?.setInput("contentTemplate", this.amendmentTemplate());
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.amendmentDialog !== ref) return;
      this.amendmentDialog = null; this.amendOpen.set(false); this.busyChanged.emit(false);
      afterNextRender(() => (this.amendmentStale() ? this.reloadDecisionTrigger() : this.amendTrigger())?.nativeElement.querySelector<HTMLElement>("button")?.focus(), { injector: this.injector });
    });
  }

  cancelAmend(): void {
    if (this.isAmending()) return;
    this.amendmentDialog?.close(); this.amendOpen.set(false);
  }

  onAmendFindingChange(value: string): void {
    this.amendFinding.set(value);
    this.amendValues.finding = value;
  }

  /** Mirror the rationale into its gate signal so Submit enables the moment it is typed. */
  onAmendRationaleChange(value: string): void {
    this.amendRationale.set(value ?? "");
  }

  /**
   * Submit the amend through the dedicated finalize endpoint — the backend supersedes the prior
   * decision while its frozen wording stays immutable. Suppressed unless the actor can author.
   */
  submitAmend(): void {
    const proceedingId = this._proceedingId();
    if (proceedingId === null || proceedingId <= 0 || !this.amendOpen() || !this.canSubmitAmend()) {
      return;
    }
    const finding = this.amendFinding();
    const specialConditions = splitLines(this.amendValues.specialConditions);
    const payload: HearingDecisionFinalizeRequest = {
      finding,
      decisionRationale: (this.amendValues.rationale ?? "").trim(),
      decidedByUserId: toPositiveInt(this.appState.idSignal()),
      captureSequence: 1,
      denialLengthYears:
        finding === "unsuitable" ? toPositiveInt(this.amendValues.denialLengthYears) : null,
      specialConditions: specialConditions.length > 0 ? specialConditions : null,
    };

    const revision = this.revision;
    this.isAmending.set(true);
    if (this.amendmentDialog) this.amendmentDialog.disableClose = true;
    this.amendError.set(null);
    this.recordService
      .finalizeDecision(proceedingId, payload)
      .pipe(
        catchError((error: unknown) => {
          if (revision !== this.revision) return of(null);
          this.isAmending.set(false);
          if (this.amendmentDialog) this.amendmentDialog.disableClose = false;
          if ((error as { status?: number })?.status === 409) this.amendmentStale.set(true);
          this.amendError.set(
            messageFromError(
              error,
              this.decisionText.amendFailed(),
              this.decisionText.amendPermissionDenied(),
            ),
          );
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (revision !== this.revision) return;
        if (result) {
          this.isAmending.set(false);
          this.amendmentDialog?.close();
          this.amendSubmitted.set(true);
          this.amendOpen.set(false);
          // Re-read so the projection reflects the supersede.
          const id = this._proceedingId();
          if (id !== null && id > 0) {
            this.load(id);
            this.loadDocuments(id);
          }
          this.amended.emit();
          // #1509: an amendment supersedes the recorded decision; the Case Snapshot rail re-reads too.
          this.caseContextRefresh.requestRefresh({
            reason: "hearing-decision-recorded",
            incarceratedPersonId: this._record()?.incarceratedPersonId ?? null,
            scheduledProceedingId: proceedingId,
          });
        }
      });
  }

  formatDate(value: string | null | undefined): string {
    const dateOnly = (value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
      return `${Number(dateOnly[2])}/${Number(dateOnly[3])}/${dateOnly[1]}`;
    }
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) {
      return "—";
    }
    return new Intl.DateTimeFormat(undefined, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }

  private load(scheduledProceedingId: number): void {
    const revision = ++this.revision;
    this.isLoading.set(true); this.loadError.set(null);
    this.recordService
      .getRecord(scheduledProceedingId)
      .pipe(
        catchError(() => { if (revision === this.revision) this.loadError.set("The decision record could not be loaded. Retry before amending."); return of(null); }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((record) => {
        if (revision !== this.revision) return;
        this.isLoading.set(false);
        if (record && record.scheduledProceedingId !== scheduledProceedingId) { this.loadError.set("The decision record does not belong to this hearing."); this._record.set(null); return; }
        this._record.set(record);
        if (record) this.amendmentStale.set(false);
      });
  }

  retryRecord(): void { const id = this._proceedingId(); if (id && !this.isAmending() && !this.externalBusy()) this.load(id); }
  private loadDocuments(scheduledProceedingId: number): void {
    const revision = ++this.documentRevision;
    this.generatedDocuments.set([]); this.documentsLoading.set(true);
    this.documentService
      .getForHearing(scheduledProceedingId)
      .pipe(
        catchError(() => {
          return of<PhmsDocument[]>([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((documents) => {
        if (revision !== this.documentRevision) return;
        this.generatedDocuments.set(
          documents.filter(
            (document) => document.documentCategoryCode === "hearing-result" || document.documentCategoryCode === "post-hearing-paperwork",
          ),
        );
        this.documentsLoading.set(false);
      });
  }
}

interface AmendFormValues {
  finding: string;
  rationale: string;
  specialConditions: string;
  denialLengthYears: string;
}

function createEmptyAmendValues(): AmendFormValues {
  return {
    finding: "",
    rationale: "",
    specialConditions: "",
    denialLengthYears: "",
  };
}

function normalizeFactorCode(factor: HearingDecisionFactor): string {
  return (factor.factorCode ?? "").trim().toLowerCase();
}

function titleCase(value: string | null | undefined): string {
  const text = (value ?? "").trim();
  if (!text) {
    return "";
  }
  return text
    .split(/[\s_-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function splitLines(value: string | null | undefined): string[] {
  return (value ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function toPositiveInt(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(String(value ?? ""));
  return Number.isInteger(parsed) && parsed > 0 && parsed <= 2147483647 ? parsed : null;
}

/** A single structured issue from the finalize/amend problem+json (code + human-readable detail). */
interface DecisionProblemIssue {
  code?: string;
  detail?: string;
}

/** A workflow decision-gate block from the finalize/amend problem+json. */
interface DecisionProblemBlock {
  requiredDecisionKind?: string;
  requiredOutcomeCode?: string;
  currentStatusCode?: string | null;
  blockReasonCode?: string | null;
  decisionRecordId?: number | null;
}

/** The problem+json body EZExceptionMiddleware emits for a blocked finalize/amend. */
interface DecisionProblemDetails {
  detail?: string;
  problemCode?: string;
  issueCodes?: string[];
  issues?: DecisionProblemIssue[];
  decisionBlocks?: DecisionProblemBlock[];
}

/**
 * Maps a finalize/amend failure to a specific, self-explanatory message (#1491). Reads the
 * problem+json the backend emits — problemCode, issueCodes[], issues[] (code + detail), detail, and
 * the workflow gate's decisionBlocks[] — so the surface names the exact violated rule and what must
 * change instead of a generic fallback. The 403 permission-denied message is preserved.
 */
function messageFromError(error: unknown, fallback: string, permissionDenied: string): string {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  if ((error as { status?: unknown }).status === 403) {
    return permissionDenied;
  }

  const problem = extractDecisionProblem(error);
  if (!problem) {
    return fallback;
  }

  const lines: string[] = [];

  for (const issue of problem.issues ?? []) {
    const detail = normalizeProblemText(issue.detail);
    const code = normalizeProblemText(issue.code);
    if (detail) {
      lines.push(code ? `${detail} (${code})` : detail);
    } else if (code) {
      lines.push(`Violated rule: ${code}.`);
    }
  }

  for (const block of problem.decisionBlocks ?? []) {
    lines.push(describeDecisionBlock(block));
  }

  if (lines.length === 0) {
    const detail = normalizeProblemText(problem.detail);
    if (detail) {
      const code = normalizeProblemText(problem.problemCode);
      lines.push(code ? `${detail} (${code})` : detail);
    } else {
      const codes = (problem.issueCodes ?? [])
        .map((value) => normalizeProblemText(value))
        .filter((value) => value.length > 0);
      if (codes.length > 0) {
        lines.push(`Violated rule: ${codes.join(", ")}.`);
      }
    }
  }

  return lines.length > 0 ? lines.join(" ") : fallback;
}

/** Pulls the problem+json body out of an HttpErrorResponse-like error (or the error itself). */
function extractDecisionProblem(error: object): DecisionProblemDetails | null {
  const body = (error as { error?: unknown }).error;
  if (isDecisionProblemLike(body)) {
    return body as DecisionProblemDetails;
  }
  if (isDecisionProblemLike(error)) {
    return error as DecisionProblemDetails;
  }
  return null;
}

function isDecisionProblemLike(value: unknown): boolean {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate["detail"] === "string" ||
    typeof candidate["problemCode"] === "string" ||
    Array.isArray(candidate["issues"]) ||
    Array.isArray(candidate["issueCodes"]) ||
    Array.isArray(candidate["decisionBlocks"])
  );
}

function describeDecisionBlock(block: DecisionProblemBlock): string {
  const kind = normalizeProblemText(block.requiredDecisionKind) || "prerequisite";
  const outcome = normalizeProblemText(block.requiredOutcomeCode) || "resolved";
  const current = normalizeProblemText(block.currentStatusCode) || "missing";
  const reason = normalizeProblemText(block.blockReasonCode);
  const reasonSuffix = reason ? ` Reason: ${reason}.` : "";
  return `This decision is blocked until the '${kind}' decision is '${outcome}' (currently ${current}).${reasonSuffix}`;
}

function normalizeProblemText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
